/**
 * Confirming an email address.
 *
 * The link in the confirmation email has always pointed here. The route did not
 * exist, so the app fell through to browse and nothing ever called the API — the
 * mail was correct, the address was never confirmed, and there was no error
 * anywhere to notice. Exactly the kind of gap that only a real person clicking a
 * real link finds.
 */
import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";

const V = window.CardboardmarketDesignSystem_2a3239;

export function VerifyScreen() {
  const { route, go, trader, notify , t } = useApp();
  const [state, setState] = React.useState('working');   // working | done | failed | missing
  const [resending, setResending] = React.useState(false);

  React.useEffect(() => {
    const token = route.token;
    if (!token) { setState('missing'); return; }
    let live = true;
    api.verifyEmail(token)
      .then(() => { if (live) setState('done'); })
      .catch(() => { if (live) setState('failed'); });
    return () => { live = false; };
    // Once, on the token it arrived with. Re-running would spend a second attempt
    // on a token that is deliberately single-use.
  }, [route.token]);

  const resend = async () => {
    setResending(true);
    try {
      const r = await api.resendVerification();
      // Same as the banner: report what the server actually did.
      if (r && r.emailSent === false) {
        notify(t("We could not send it just now. Try again in a few minutes."), 'caution');
        return;
      }
      notify(t("Sent. Check your inbox — and your spam folder."));
    } catch (e) {
      notify(e.message, 'caution');
    } finally { setResending(false); }
  };

  if (state === 'working') {
    return <V.EmptyState icon="clock" title={t("Confirming your email…")} description={t("One moment.")} />;
  }

  if (state === 'done') {
    return (
      <V.EmptyState
        icon="check"
        title={t("Email confirmed")}
        description={t("That is everything. You can post cards and message traders now.")}
        action={<V.Button onClick={() => go('browse')}>{t("Browse nearby cards")}</V.Button>}
      />
    );
  }

  // A used, expired or unknown token all land here, and deliberately look the
  // same: which one it was is not something to tell whoever is holding it.
  return (
    <V.EmptyState
      icon="x"
      title={state === 'missing' ? t("That link is incomplete") : t("That link has expired")}
      description={
        state === 'missing'
          ? t("The confirmation link seems to have been cut short. Ask for a new one and use the whole thing.")
          : t("Confirmation links are single-use and last a few hours. Ask for a fresh one.")
      }
      action={trader
        ? <V.Button onClick={resend} disabled={resending}>{resending ? t("Sending\u2026") : t("Send me a new link")}</V.Button>
        : <V.Button onClick={() => go('onboarding')}>{t("Sign in to get a new link")}</V.Button>}
    />
  );
}

